/* eslint-disable @typescript-eslint/no-use-before-define */
import pDebounce from 'p-debounce'
import _ from 'lodash'
import { enableAllPlugins, produceWithPatches, Patch, Draft } from 'immer'
import { useStore } from './store'
import { pagesToDocs } from './utils'
import type {
    ImageId,
    ImageIds,
    ImageUrl,
    PagesFormState,
    DwtDocuments,
    DwtStore,
    BarcodeInfo,
    DocsSettings,
} from './types'

const PDEBOUNCE_MS = 2000
const PDEBOUNCE_OPTS = { leading: true }

// Immer setup
/* eslint-disable no-param-reassign */
enableAllPlugins()

// Immer state updates
// const immutableSet = (fn: (store: Store) => void): void =>
//   useStore.setState(produce(fn))

// Immer state updates via patches
const forwardPatches: Patch[] = []
const backPatches: Patch[] = []
const wrapWithPatch = (fn: (draft: Draft<DwtStore>) => void) => (store: DwtStore) => {
    const [newStore, patches, invPatches] = produceWithPatches(fn)(store)
    console.debug('wrapWithPatch', { newStore, patches, invPatches })
    forwardPatches.push(...patches)
    backPatches.push(...invPatches)
    return newStore
}

// Overwriting Setters
export type SetBufferImageIdsParams = [imageIds: ImageIds, debugName?: string]
export const setBufferImageIds = async (imageIds: ImageIds, debugName = 'setBufferImageIds'): Promise<ImageIds> => {
    console.debug('setBufferImageIds')
    await Promise.resolve()
    console.debug('setBufferImageIds', imageIds)
    useStore.getState().namedSet(
        wrapWithPatch((draft) => {
            draft.bufferImageIds = imageIds
        }),
        false,
        debugName,
    )
    console.debug('setBufferImageIds patched', imageIds)
    await updateComputedDocsNow()
    console.debug('setBufferImageIds set', imageIds)
    return imageIds
}

export type SetPagesFormStateParams = [fs: PagesFormState, debugName?: string]
export const setPagesFormState = async (
    fs: PagesFormState,
    debugName = 'setPagesFormState',
): Promise<PagesFormState> => {
    await Promise.resolve()
    console.debug('setPagesFormState', fs)

    // Immer deep-freezes with side-effects. Don't break incoming props
    // https://immerjs.github.io/immer/freezing/
    const clonedFs = _.cloneDeep(fs)
    // Alternative, without undefineds
    // const cleanFs = Object.fromEntries(
    //   Object.entries(fs).map(([id, obj]) => [
    //     id,
    //     Object.fromEntries(
    //       Object.entries(obj).filter(([, v]) => v !== undefined),
    //     ),
    //   ]),
    // )

    useStore.getState().namedSet(
        wrapWithPatch((draft) => {
            Object.assign(draft.pagesFormState, clonedFs)
        }),
        false,
        debugName,
    )
    console.debug('setPagesFormState patched', fs)
    await updateComputedDocs()
    console.debug('setPagesFormState set', fs)
    return fs
}

// Setters
export type SetImageHrefProps = { imageId: ImageId; imageHref: ImageUrl }
export type SetImageHrefParams = [props: SetImageHrefProps, debugName?: string]
export const setImageHref = async (
    { imageId, imageHref }: SetImageHrefProps,
    debugName = 'setImageHref',
): Promise<ImageUrl> => {
    await Promise.resolve()
    useStore.getState().namedSet(
        wrapWithPatch((draft) => {
            draft.imageHrefs.set(imageId, imageHref)
        }),
        false,
        debugName,
    )
    await updateComputedDocs()
    return imageHref
}

export type SetImageSrcProps = { imageId: ImageId; imageSrc: ImageUrl }
export type SetImageSrcParams = [props: SetImageSrcProps, debugName?: string]
export const setImageSrc = async (
    { imageId, imageSrc }: SetImageSrcProps,
    debugName = 'setImageSrc',
): Promise<ImageUrl> => {
    await Promise.resolve()
    useStore.getState().namedSet(
        wrapWithPatch((draft) => {
            draft.imageSrcs.set(imageId, imageSrc)
        }),
        false,
        debugName,
    )
    await updateComputedDocs()
    return imageSrc
}

export type SetBarcodeProps = { imageId: ImageId; barcode: BarcodeInfo }
export type SetBarcodeParams = [props: SetBarcodeProps, debugName?: string]
export const setBarcode = async (
    { imageId, barcode }: SetBarcodeProps,
    debugName = 'setBarcode',
): Promise<BarcodeInfo | null> => {
    await Promise.resolve()
    useStore.getState().namedSet(
        wrapWithPatch((draft) => {
            draft.barcodes.set(imageId, barcode)
        }),
        false,
        debugName,
    )
    await updateComputedDocs()
    return barcode
}

export type SetBlankProps = { imageId: ImageId; isBlank: boolean }
export type SetBlankParams = [props: SetBlankProps, debugName?: string]
export const setBlank = async ({ imageId, isBlank }: SetBlankProps, debugName = 'setBlank'): Promise<boolean> => {
    await Promise.resolve()
    useStore.getState().namedSet(
        wrapWithPatch((draft) => {
            draft.blanks.set(imageId, isBlank)
        }),
        false,
        debugName,
    )
    await updateComputedDocs()
    return isBlank
}

export type SetSettingsProps = DocsSettings
export type SetSettingsParams = [props: SetSettingsProps, debugName?: string]
export const setSettings = async (settings: SetSettingsProps, debugName = 'setSettings'): Promise<DocsSettings> => {
    await Promise.resolve()
    useStore.getState().namedSet(
        wrapWithPatch((draft) => {
            draft.settings.autoCatalog = settings.autoCatalog
            draft.settings.barcodeReader = settings.barcodeReader
            draft.settings.blankPageSeparator = settings.blankPageSeparator
            draft.settings.ignoreBlankPages = settings.ignoreBlankPages
        }),
        false,
        debugName,
    )
    return settings
}

export type SetBarcodeReaderProps = DocsSettings['barcodeReader']
export type SetBarcodeReaderParams = [props: SetBarcodeReaderProps, debugName?: string]
export const setBarcodeReader = async (
    setting: SetBarcodeReaderProps,
    debugName = 'setBarcodeReader',
): Promise<SetBarcodeReaderProps> => {
    await Promise.resolve()
    useStore.getState().namedSet(
        wrapWithPatch((draft) => {
            draft.settings.barcodeReader = setting
        }),
        false,
        debugName,
    )
    return setting
}

export type SetBlankPageSeperatorProps = DocsSettings['blankPageSeparator']
export type SetBlankPageSeperatorParams = [props: SetBlankPageSeperatorProps, debugName?: string]
export const setBlankPageSeparator = async (
    setting: SetBlankPageSeperatorProps,
    debugName = 'setBlankPageSeparator',
): Promise<SetBlankPageSeperatorProps> => {
    await Promise.resolve()
    useStore.getState().namedSet(
        wrapWithPatch((draft) => {
            draft.settings.blankPageSeparator = setting
        }),
        false,
        debugName,
    )
    return setting
}

export type SetComputedDocsParams = [docs: DwtDocuments, debugName?: string]
export const setComputedDocs = async (docs: DwtDocuments, debugName = 'setComputedDocs'): Promise<DwtDocuments> => {
    await Promise.resolve()
    console.debug('setComputedDocs')
    useStore.getState().namedSet(
        wrapWithPatch((draft) => {
            draft.computedDocs = docs
        }),
        false,
        debugName,
    )
    console.debug('setComputedDocs', docs)
    return docs
}

export const updateComputedDocsNow = async (): Promise<DwtDocuments> => {
    await Promise.resolve()
    console.debug('updateComputedDocs')
    const state = useStore.getState()
    const { bufferImageIds, pagesFormState, barcodes, blanks, settings } = state
    console.debug('updateComputedDocs', state)
    const docs = pagesToDocs({
        imageIds: bufferImageIds,
        formState: pagesFormState,
        barcodes,
        blanks,
        autoCatalog: settings.autoCatalog,
        ignoreBlankPages: settings.ignoreBlankPages,
    })
    console.debug('updateComputedDocs docs', docs)
    await setComputedDocs(docs)
    console.debug('updateComputedDocs set', docs)
    return docs
}

export const updateComputedDocs = pDebounce<never, DwtDocuments>(updateComputedDocsNow, PDEBOUNCE_MS, PDEBOUNCE_OPTS)
